import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext();

const STORAGE_KEY = "gamestrade_cart";
const PREORDER_STORAGE_KEY = "gamestrade_preorder_cart";
const DIGITAL_PROCESSING_RATE = 0.025;

const isDigitalItem = (item) => item.category === "digital";
const calculateDigitalProcessingFee = (items) =>
  Math.round(items.reduce((s, p) => s + (isDigitalItem(p) ? p.price * p.quantity : 0), 0) * DIGITAL_PROCESSING_RATE * 100) / 100;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const parsed = saved ? JSON.parse(saved) : [];
      // Strip any preorder items that snuck into the regular cart
      return parsed.filter((p) => !p.is_preorder);
    } catch {
      return [];
    }
  });

  const [preOrderItems, setPreOrderItems] = useState(() => {
    try {
      const preOrderSaved = localStorage.getItem(PREORDER_STORAGE_KEY);
      const existing = preOrderSaved ? JSON.parse(preOrderSaved) : [];
      // Migrate preorder items from regular cart into preorder cart (keep first unique product only)
      const regularSaved = localStorage.getItem(STORAGE_KEY);
      const regular = regularSaved ? JSON.parse(regularSaved) : [];
      const preorders = regular.filter((p) => p.is_preorder);
      if (preorders.length === 0) return existing;
      const merged = [...existing];
      for (const item of preorders) {
        if (!merged.find((p) => p.product_id === item.product_id) && merged.length === 0) {
          merged.push(item);
        }
      }
      return merged;
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(PREORDER_STORAGE_KEY, JSON.stringify(preOrderItems));
  }, [preOrderItems]);

  // On mount: if any pre-order item's release_date has passed, move it into the regular cart
  useEffect(() => {
    try {
      const savedPre = localStorage.getItem(PREORDER_STORAGE_KEY);
      const preList = savedPre ? JSON.parse(savedPre) : [];
      if (!preList || preList.length === 0) return;
      const now = new Date();
      const expired = preList.filter((p) => p.release_date && new Date(p.release_date) <= now);
      if (expired.length === 0) return;

      const remaining = preList.filter((p) => !(p.release_date && new Date(p.release_date) <= now));

      // Merge expired into regular cart (add or increase qty)
      setItems((prev) => {
        const copy = [...prev];
        for (const e of expired) {
          const exists = copy.find((i) => i.product_id === e.product_id);
          if (exists) {
            exists.quantity = exists.quantity + (e.quantity || 1);
          } else {
            copy.push({
              product_id: e.product_id,
              name: e.name,
              price: e.price,
              image: e.image,
              category: e.category || "",
              condition: e.condition || "new",
              platform: e.platform || "",
              is_preorder: false,
              quantity: e.quantity || 1,
            });
          }
        }
        return copy;
      });

      setPreOrderItems(remaining);
    } catch (e) {
      // ignore
    }
  }, []);

  // ── Regular cart ──────────────────────────────────────────────
  const addToCart = (product, qty = 1) => {
    if (product.is_preorder) return addToPreOrderCart(product, qty);
    setItems((prev) => {
      const existing = prev.find((p) => p.product_id === product.id);
      if (existing) {
        return prev.map((p) =>
          p.product_id === product.id ? { ...p, quantity: p.quantity + qty } : p
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: product.images?.[0],
          category: product.category,
          condition: product.condition,
          platform: product.platform,
          quantity: qty,
        },
      ];
    });
  };

  const updateQty = (product_id, quantity) => {
    if (quantity <= 0) return removeFromCart(product_id);
    setItems((prev) => prev.map((p) => (p.product_id === product_id ? { ...p, quantity } : p)));
  };

  const removeFromCart = (product_id) => {
    setItems((prev) => prev.filter((p) => p.product_id !== product_id));
  };

  const clearCart = () => setItems([]);

  const subtotal = items.reduce((s, p) => s + p.price * p.quantity, 0);
  const hasPhysicalItems = items.some((p) => !isDigitalItem(p));
  const digitalProcessingFee = calculateDigitalProcessingFee(items);
  const shipping = !hasPhysicalItems || subtotal > 4999 || subtotal === 0 ? 0 : 100;
  const total = subtotal + shipping + digitalProcessingFee;
  const count = items.reduce((s, p) => s + p.quantity, 0);

  // ── Preorder cart (max 1 unique product) ──────────────────────
  // Returns true if added, false if already has a different product
  const addToPreOrderCart = (product, qty = 1) => {
    let result = false;
    setPreOrderItems((prev) => {
      const existing = prev.find((p) => p.product_id === product.id);
      if (existing) {
        const newQty = Math.min(existing.quantity + qty, product.stock);
        result = "updated";
        return prev.map((p) =>
          p.product_id === product.id ? { ...p, quantity: newQty } : p
        );
      }
      // Block if a different product already exists
      if (prev.length > 0) {
        result = "blocked";
        return prev;
      }
      result = "added";
      return [
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: product.images?.[0],
          condition: product.condition,
          platform: product.platform,
          release_date: product.release_date || null,
          expected_stock: product.stock,
          quantity: Math.min(qty, product.stock),
        },
      ];
    });
    return result;
  };

  const updatePreOrderQty = (product_id, quantity) => {
    if (quantity <= 0) return removeFromPreOrderCart(product_id);
    setPreOrderItems((prev) =>
      prev.map((p) => {
        if (p.product_id !== product_id) return p;
        const capped = p.expected_stock ? Math.min(quantity, p.expected_stock) : quantity;
        return { ...p, quantity: capped };
      })
    );
  };

  const removeFromPreOrderCart = (product_id) => {
    setPreOrderItems((prev) => prev.filter((p) => p.product_id !== product_id));
  };

  const clearPreOrderCart = () => setPreOrderItems([]);

  const preOrderSubtotal = preOrderItems.reduce((s, p) => s + p.price * p.quantity, 0);
  const preOrderCount = preOrderItems.reduce((s, p) => s + p.quantity, 0);

  return (
    <CartContext.Provider value={{
      // Regular cart
      items, addToCart, updateQty, removeFromCart, clearCart,
      subtotal, shipping, digitalProcessingFee, total, count,
      // Preorder cart
      preOrderItems, addToPreOrderCart, updatePreOrderQty, removeFromPreOrderCart, clearPreOrderCart,
      preOrderSubtotal, preOrderCount,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
